import type { Client, LeadTemperature } from '../services/crmService';

/**
 * Heuristic score used when API `lead_score` is absent; mirrors backend `crm/scoring.compute_pipeline_contact_score`.
 */
export function getLeadScore(c: Client): number {
  let score = 0;
  if (c.company_name) score += 20;
  if (c.phone) score += 15;
  if (c.job_title) score += 10;
  if (c.tags && c.tags.length > 0) score += 5;
  if (c.source === 'referral') score += 25;
  if (c.source === 'website') score += 15;
  return Math.min(score, 100);
}

/** Thresholds must match `backend/crm/scoring.py` (`lead_temperature_from_score`). */
export function getLeadTemperatureFromScore(score: number | null | undefined): LeadTemperature {
  if (score == null || Number.isNaN(score)) return 'cold';
  if (score >= 70) return 'hot';
  if (score >= 40) return 'warm';
  return 'cold';
}

export function getDisplayLeadScore(c: Client): number {
  return c.lead_score ?? getLeadScore(c);
}

export function getDisplayLeadTemperature(c: Client): LeadTemperature {
  return c.lead_temperature ?? getLeadTemperatureFromScore(getDisplayLeadScore(c));
}

const TEMP_ORDER: Record<LeadTemperature, number> = { hot: 0, warm: 1, cold: 2 };

/** Hot first, then warm, then cold; within band sort by score descending. */
export function comparePipelinePriority(a: Client, b: Client): number {
  const ta = getDisplayLeadTemperature(a);
  const tb = getDisplayLeadTemperature(b);
  if (TEMP_ORDER[ta] !== TEMP_ORDER[tb]) return TEMP_ORDER[ta] - TEMP_ORDER[tb];
  return getDisplayLeadScore(b) - getDisplayLeadScore(a);
}

export function averageScore(clients: Client[]): number | null {
  if (!clients.length) return null;
  const sum = clients.reduce((acc, c) => acc + getDisplayLeadScore(c), 0);
  return Math.round(sum / clients.length);
}
